import { WikiLayout } from "@/components/WikiLayout";
import { WikiPage } from "@/components/WikiPage";

const ResourceFAQ = () => {
  return (
    <WikiLayout>
      <WikiPage 
        title="FAQ & Troubleshooting" 
        description="Common questions and quick answers"
      >
        <section className="space-y-6">
          <p>
            Quick answers to the most frequently asked questions from BlueLux independent contractors. 
            If you don't find your answer here, contact the office.
          </p>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Getting Started</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-foreground">When do I get my first payment?</h3>
              <p className="text-muted-foreground">
                Payments are made bi-weekly (every two weeks) for all completed services. You'll receive 
                your first payment approximately 2 weeks after completing your first service week. Payments 
                are made via direct deposit or check based on your preference.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">What if I don't have a truck?</h3>
              <p className="text-muted-foreground">
                You need a vehicle capable of carrying pool equipment and chemicals. This can be a truck, 
                van, or large SUV. Sedans and small cars typically don't have sufficient cargo space. 
                See the Vehicle Requirements page for details.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">Do I need pool experience to be hired?</h3>
              <p className="text-muted-foreground">
                No! We provide comprehensive training. Most successful technicians had no pool experience 
                when they started. What matters more is reliability, attention to detail, and willingness to learn.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Daily Operations</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-foreground">What if I'm running late for my route?</h3>
              <p className="text-muted-foreground">
                Call or text the office as soon as you know you'll be late. Inform them of expected delay 
                and reason. Most customers are understanding about occasional delays, especially if we 
                communicate proactively.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">Can I rearrange the order of stops on my route?</h3>
              <p className="text-muted-foreground">
                Yes! You know your area best. Optimize your route for efficiency, but be mindful of 
                customers who have time preferences (e.g., must be serviced before 2 PM). Document any 
                regular route sequence changes with the office.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">What if a customer's dog is aggressive?</h3>
              <p className="text-muted-foreground">
                Your safety comes first. Do NOT attempt to service the pool if you feel threatened. 
                Leave the property and notify the office immediately. We'll contact the customer to make 
                arrangements for the dog to be secured during future visits.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">Customer isn't home and gate is locked—what do I do?</h3>
              <p className="text-muted-foreground">
                Check the customer file for gate code or access instructions. If you can't find it, call/text 
                the office—they may have it on file. If customer is unreachable and you can't access the pool, 
                document "unable to access" and you'll still receive 50% of the base rate for that stop.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Technical Questions</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-foreground">What if the pool is really dirty—do I spend extra time?</h3>
              <p className="text-muted-foreground">
                Yes, quality is priority. If a pool requires significantly more time than usual (30+ min extra), 
                document the condition with photos and note the extended time in your service report. You may 
                be compensated additional time, and we can recommend a special cleaning service to the customer.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">Can I shock a pool during the day?</h3>
              <p className="text-muted-foreground">
                It's best to shock at dusk when UV light won't degrade the chlorine, but if a pool desperately 
                needs shocking during your service, go ahead. Just inform the customer that it's most effective 
                if added in the evening and recommend they run their pump overnight.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">What if I don't know what's wrong with equipment?</h3>
              <p className="text-muted-foreground">
                Don't guess! Take photos/video of the issue and send to the Field Supervisor or office. 
                Describe what you're observing (noises, leaks, error codes, etc.). Someone will guide you 
                or we'll schedule a specialized technician. It's better to ask than to misdiagnose.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">Do I have to vacuum every pool every time?</h3>
              <p className="text-muted-foreground">
                Not necessarily. Vacuum when needed—typically when you see debris on the bottom that can't be 
                brushed to the main drain. In summer with lots of leaves, you may vacuum weekly. In winter 
                with minimal debris, every other week might be fine. Use your judgment.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Pay & Benefits</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-foreground">How do I track my earnings?</h3>
              <p className="text-muted-foreground">
                The mobile app tracks all completed stops automatically. You can view your estimated earnings 
                in real-time. At the end of each pay period, you'll receive a detailed pay statement showing 
                all stops, commissions, and bonuses.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">Can I work extra days to earn more?</h3>
              <p className="text-muted-foreground">
                Yes! If you want to pick up additional stops or cover for another technician, let the office 
                know you're available for extra work. You can also earn more by selling add-on services and 
                equipment repairs to your existing customers.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">Do I get paid for drive time?</h3>
              <p className="text-muted-foreground">
                Your per-stop rate includes reasonable drive time between stops. Routes are designed to minimize 
                driving. You also receive a $200/month vehicle allowance that helps cover fuel costs.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">What happens to my route if I take time off?</h3>
              <p className="text-muted-foreground">
                Another contractor will cover your route while you're unavailable. Your customers will still be serviced 
                and you don't lose your route assignment. When you return, you resume your regular schedule. As an 
                independent contractor, you're not paid for time you don't work.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Customer Relations</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-foreground">Customer wants me to do extra work "off the books" for cash—should I?</h3>
              <p className="text-muted-foreground">
                <strong className="text-destructive">Absolutely not.</strong> All services must go through BlueLux 
                for liability and insurance reasons. Politely decline and explain that you can set up any additional 
                services through the office. Side work can result in immediate termination.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">Customer is flirting/being inappropriate—how do I handle it?</h3>
              <p className="text-muted-foreground">
                Stay professional and polite but firmly maintain boundaries. If behavior persists or makes you 
                uncomfortable, document it and report to management immediately. We will address it with the 
                customer or reassign the account if necessary. Your safety and comfort are priorities.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">What if customer complains about my work?</h3>
              <p className="text-muted-foreground">
                Listen calmly and acknowledge their concern. Don't get defensive. Explain what you did and why. 
                If they're still unsatisfied, let them know you'll have your supervisor contact them to resolve 
                the issue. Document the complaint and notify the office same day.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">Can I accept tips from customers?</h3>
              <p className="text-muted-foreground">
                Yes! Small tips and holiday gifts are acceptable and appreciated. However, never provide service 
                expecting tips, and don't accept large gifts (over $50) that could be seen as trying to influence 
                your service or create a conflict of interest.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Mobile App & Technology</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-foreground">My app won't load or is glitching—what do I do?</h3>
              <p className="text-muted-foreground">
                First, try closing and reopening the app. If that doesn't work, check your phone's data connection. 
                Still having issues? Call the office—they can access the system to help troubleshoot or you can 
                temporarily use paper service forms as backup.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">I accidentally marked a stop complete when I haven't serviced it yet—can I fix it?</h3>
              <p className="text-muted-foreground">
                Yes! Call the office immediately to have them reopen the stop. Then complete the service properly. 
                Don't try to skip it and hope no one notices—GPS tracking will show you didn't actually visit 
                the location.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">Does the company track my location via GPS?</h3>
              <p className="text-muted-foreground">
                Yes, the mobile app uses GPS to verify stop completion and optimize routing. This protects both 
                you and the company. Location tracking is only active during work hours and can be turned off 
                when you're off the clock.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Safety & Emergencies</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-foreground">What if I spill chemicals in my vehicle?</h3>
              <p className="text-muted-foreground">
                Pull over safely and ventilate the vehicle immediately (open all doors/windows). Use your spill 
                kit to contain and neutralize the spill. If spill is large or involves acid/chlorine mixing, 
                evacuate the vehicle and call 911 and then the office. Do not continue route until vehicle is 
                properly cleaned.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">I accidentally damaged customer property—what now?</h3>
              <p className="text-muted-foreground">
                Be honest immediately. Apologize to the customer and notify the office right away. Take photos 
                of the damage. BlueLux's liability insurance covers accidental damage, but we need to know 
                about it promptly. Trying to hide damage always makes things worse.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground">It's dangerously hot today—can I refuse to work?</h3>
              <p className="text-muted-foreground">
                If conditions are truly dangerous (extreme heat warnings, air quality alerts), contact the office. 
                We may delay or cancel routes for safety. However, pool work is outdoor work in summer heat, so 
                take frequent breaks, stay hydrated, and work during cooler morning hours when possible.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Still Have Questions?</h2>
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-6">
            <p className="font-medium mb-2">Can't find your answer?</p>
            <p className="text-muted-foreground mb-4">
              Reach out to us—we're here to help!
            </p>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><strong>Office:</strong> (619) 555-0100</li>
              <li><strong>Email:</strong> info@bluelux.com</li>
              <li><strong>Field Supervisor Cell:</strong> (619) 555-0102</li>
            </ul>
          </div>
        </section>
      </WikiPage>
    </WikiLayout>
  );
};

export default ResourceFAQ;